import React from "react";
import { MapPin, ExternalLink } from "lucide-react";
import { SiteDetails } from "../../data/sitelocations";

interface MapPreviewProps {
  siteInfo: SiteDetails;
  onOpenMap: () => void;
  onClose: () => void;
}

const MapPreview: React.FC<MapPreviewProps> = ({ siteInfo, onOpenMap }) => {
  const getGoogleMapsEmbedUrl = (): string => {
    const { lat, lon } = siteInfo.coordinates;
    
    // Always prioritize coordinates from database - generate map URL from lat/lon
    // This ensures the map shows the location entered by admin during site onboarding
    // Only use map_embed as fallback if coordinates are invalid (shouldn't happen with valid data)
    if (lat && lon && !isNaN(lat) && !isNaN(lon) && lat !== 0 && lon !== 0) {
      // Use coordinates to generate fresh map embed URL
      return `https://maps.google.com/maps?q=${lat},${lon}&t=k&z=16&output=embed`;
    }
    
    // Fallback to map_embed only if coordinates are not available
    if (siteInfo.mapEmbed) {
      if (siteInfo.mapEmbed.includes('<iframe')) {
        const srcMatch = siteInfo.mapEmbed.match(/src="([^"]+)"/);
        if (srcMatch && srcMatch[1]) {
          return srcMatch[1];
        }
      } else {
        return siteInfo.mapEmbed;
      }
    }
    
    // Final fallback - use coordinates even if they seem invalid
    return `https://maps.google.com/maps?q=${lat},${lon}&t=k&z=16&output=embed`;
  };

  return (
    <div
      className="absolute right-0 top-full mt-[-6px] bg-white/98 backdrop-blur-xl rounded-2xl shadow-2xl border border-[#C2DFF1]/60 overflow-hidden"
      style={{ 
        width: "380px",
        zIndex: 10000
      }}
    >
      <div className="p-3 bg-gradient-to-r from-[#F2F8FC]/95 to-[#F2F8FC]/95 border-b border-[#C2DFF1]/50">
        <div className="flex items-center justify-between">
          <div className="flex-1 min-w-0">
            <h4 className="text-sm font-bold text-gray-800 flex items-center gap-2 truncate">
              <MapPin className="w-4 h-4 text-[#0084CF] flex-shrink-0" />
              <span className="truncate">{siteInfo.displayName}</span>
            </h4>
            <p className="text-xs text-gray-600 mt-0.5">
              {siteInfo.coordinates.lat.toFixed(4)}, {siteInfo.coordinates.lon.toFixed(4)}
            </p>
          </div>
          <button
            onClick={onOpenMap}
            className="p-2 bg-[#E3F0F9]/80 hover:bg-[#C2DFF1]/80 rounded-xl transition-colors flex-shrink-0 ml-2"
            title="Open in Google Maps"
          >
            <ExternalLink className="w-4 h-4 text-[#0066A3]" />
          </button>
        </div>
      </div>
      <div className="relative">
        <iframe
          src={getGoogleMapsEmbedUrl()}
          width="380"
          height="180"
          style={{ border: 0 }}
          allowFullScreen
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          className="w-full"
          title={siteInfo.displayName}
        />
        <div className="absolute bottom-2 left-2 bg-white/95 backdrop-blur-sm rounded-lg px-3 py-1.5 shadow-sm border border-[#E3F0F9]">
          <span className="text-xs font-medium text-gray-700">
            Click button to open full map
          </span>
        </div>
      </div>
    </div>
  );
};

export default MapPreview;
